import { supabase } from "@/integrations/supabase/client";

// Output shape consumed by ResumeMatchAnalysisModal / ClaudeMatchAnalysisModal.
export interface MatchScoreItem { before: number; after: number; change: string; }
export interface MappedAnalysisResults {
  title?: string;
  overview?: string;
  scores?: Record<string, MatchScoreItem>;
  problems_you_now_solve?: { summary?: string; problems?: Array<{ their_problem: string; your_solution: string; where_it_appears: string; what_changed: string; }>; };
  keywords_added?: { summary?: string; coverage_percentage?: number; keywords_added?: string[]; keywords_still_missing?: string[]; };
  why_this_matters?: { before?: string; after?: string; };
  metadata?: { target_role?: string; target_company?: string; analysis_date?: string; };
}

export interface HybridMatchResult {
  beforeScore: number | null;
  afterScore: number | null;
  analysisResults: MappedAnalysisResults | null;
}

// deno-lint-ignore no-explicit-any
type ClaudeMatch = any;

const toText = (v: unknown): string => {
  if (!v) return "";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v, null, 2); } catch { return String(v); }
};

// One analyze-resume-match call in resume_job_match mode.
async function analyzeOne(resumeText: string, jobDescriptionText: string): Promise<ClaudeMatch | null> {
  const { data, error } = await supabase.functions.invoke("analyze-resume-match", {
    body: { action: "resume_job_match", resumeText, job_description_text: jobDescriptionText },
  });
  if (error) { console.error("[claudeMatchAnalysis] analyze error:", error); return null; }
  if (!data?.success || !data?.result) { console.error("[claudeMatchAnalysis] no result:", data); return null; }
  return data.result as ClaudeMatch;
}

// Fast score-only call (~2s) — returns just the rubric category scores.
async function scoreOne(resumeText: string, jobDescriptionText: string): Promise<ClaudeMatch | null> {
  const { data, error } = await supabase.functions.invoke("analyze-resume-match", {
    body: { action: "resume_job_match", score_only: true, resumeText, job_description_text: jobDescriptionText },
  });
  if (error) { console.error("[claudeMatchAnalysis] score error:", error); return null; }
  if (!data?.success || !data?.result) { console.error("[claudeMatchAnalysis] no score result:", data); return null; }
  return data.result as ClaudeMatch;
}

const clamp = (n: number) => Math.max(0, Math.min(100, Math.round(n)));

// Derive a 0-100 keyword-coverage number from the missing-keyword list.
function keywordCoverage(missing: ClaudeMatch[] | undefined): number {
  if (!missing || missing.length === 0) return 95;
  const penalty = missing.reduce((acc, k) => acc + (k.importance === "Critical" ? 9 : k.importance === "Important" ? 5 : 2), 0);
  return clamp(100 - penalty);
}

// Derive a 0-100 problem-positioning number from company-problem coverage.
function problemPositioning(coverage: ClaudeMatch[] | undefined): number {
  if (!coverage || coverage.length === 0) return 60;
  const per = 100 / coverage.length;
  const total = coverage.reduce((acc, c) => acc + (c.resume_coverage === "Directly Addressed" ? per : c.resume_coverage === "Partially Addressed" ? per * 0.55 : 0), 0);
  return clamp(total);
}

const fmtChange = (b: number, a: number) => `${a - b >= 0 ? "+" : ""}${a - b}`;

/**
 * FAST phase (~2-3s): just the before/after scores plus the rubric sub-bars,
 * so the donuts render almost immediately. The detailed analysis (keywords,
 * problems, recommendations) comes from runHybridMatchAnalysis afterward.
 */
export async function runHybridScores(opts: {
  originalResumeText: unknown;
  tailoredResumeText: unknown;
  jobDescriptionText: unknown;
  jobTitle?: string;
  companyName?: string;
}): Promise<HybridMatchResult> {
  const original = toText(opts.originalResumeText).trim();
  const tailored = toText(opts.tailoredResumeText).trim();
  const jd = toText(opts.jobDescriptionText).trim();
  if (!original || !tailored || !jd) return { beforeScore: null, afterScore: null, analysisResults: null };

  const [O, T] = await Promise.all([scoreOne(original, jd), scoreOne(tailored, jd)]);
  if (!O || !T) return { beforeScore: null, afterScore: null, analysisResults: null };

  const beforeScore = clamp(O.ats_match_score?.score ?? 0);
  const afterScore = clamp(T.ats_match_score?.score ?? 0);
  const sub = (m: ClaudeMatch, cat: string): number => clamp(m?.category_scores?.[cat]?.score ?? 0);

  const analysisResults: MappedAnalysisResults = {
    title: "Resume Match Analysis",
    scores: {
      overall: { before: beforeScore, after: afterScore, change: fmtChange(beforeScore, afterScore) },
      keyword_coverage: { before: sub(O, "keywords"), after: sub(T, "keywords"), change: fmtChange(sub(O, "keywords"), sub(T, "keywords")) },
      skills_match: { before: sub(O, "requirements"), after: sub(T, "requirements"), change: fmtChange(sub(O, "requirements"), sub(T, "requirements")) },
      experience_relevance: { before: sub(O, "experience"), after: sub(T, "experience"), change: fmtChange(sub(O, "experience"), sub(T, "experience")) },
    },
    metadata: { target_role: opts.jobTitle, target_company: opts.companyName, analysis_date: new Date().toISOString() },
  };

  return { beforeScore, afterScore, analysisResults };
}

/**
 * Hybrid path: VectorShift produced the documents but no scores. Run Claude's
 * analyze-resume-match on the ORIGINAL and the TAILORED resume (in parallel),
 * then map the two results into the before/after AnalysisResults the modal
 * already knows how to render.
 */
export async function runHybridMatchAnalysis(opts: {
  originalResumeText: unknown;
  tailoredResumeText: unknown;
  jobDescriptionText: unknown;
  jobTitle?: string;
  companyName?: string;
}): Promise<HybridMatchResult> {
  const original = toText(opts.originalResumeText).trim();
  const tailored = toText(opts.tailoredResumeText).trim();
  const jd = toText(opts.jobDescriptionText).trim();

  if (!original || !tailored || !jd) {
    console.warn("[claudeMatchAnalysis] missing inputs", { hasOriginal: !!original, hasTailored: !!tailored, hasJd: !!jd });
    return { beforeScore: null, afterScore: null, analysisResults: null };
  }

  const [O, T] = await Promise.all([analyzeOne(original, jd), analyzeOne(tailored, jd)]);
  if (!O || !T) return { beforeScore: null, afterScore: null, analysisResults: null };

  const beforeScore = clamp(O.ats_match_score?.score ?? 0);
  const afterScore = clamp(T.ats_match_score?.score ?? 0);

  // Prefer Claude's deterministic category_scores; fall back to local heuristics
  // so older responses (no category_scores) still render.
  const catScore = (m: ClaudeMatch, cat: string, fallback: number): number => {
    const s = m?.category_scores?.[cat]?.score;
    return typeof s === "number" ? clamp(s) : fallback;
  };
  const kcO = catScore(O, "keywords", keywordCoverage(O.missing_keywords));
  const kcT = catScore(T, "keywords", keywordCoverage(T.missing_keywords));
  const reqO = catScore(O, "requirements", problemPositioning(O.company_problems_coverage));
  const reqT = catScore(T, "requirements", problemPositioning(T.company_problems_coverage));
  const ppO = problemPositioning(O.company_problems_coverage);
  const ppT = problemPositioning(T.company_problems_coverage);
  const heO = catScore(O, "experience", clamp(O.human_eye_score?.score ?? beforeScore));
  const heT = catScore(T, "experience", clamp(T.human_eye_score?.score ?? afterScore));

  // Keywords the tailored resume now covers that the original was missing.
  const tailoredMissing = new Set((T.missing_keywords || []).map((k: ClaudeMatch) => String(k.keyword).toLowerCase()));
  const keywordsAdded = (O.missing_keywords || [])
    .map((k: ClaudeMatch) => String(k.keyword))
    .filter((kw: string) => !tailoredMissing.has(kw.toLowerCase()));

  const analysisResults: MappedAnalysisResults = {
    title: "Resume Match Analysis",
    overview: T.tailoring_verdict?.verdict_reasoning || "",
    scores: {
      overall: { before: beforeScore, after: afterScore, change: fmtChange(beforeScore, afterScore) },
      keyword_coverage: { before: kcO, after: kcT, change: fmtChange(kcO, kcT) },
      skills_match: { before: reqO, after: reqT, change: fmtChange(reqO, reqT) },
      experience_relevance: { before: heO, after: heT, change: fmtChange(heO, heT) },
      problem_solution_positioning: { before: ppO, after: ppT, change: fmtChange(ppO, ppT) },
    },
    problems_you_now_solve: {
      summary: T.tailoring_verdict?.verdict_reasoning || "",
      problems: (T.company_problems_coverage || []).map((c: ClaudeMatch) => ({
        their_problem: [c.problem_title, c.problem_description].filter(Boolean).join(": "),
        your_solution: c.coverage_evidence || "",
        where_it_appears: c.resume_coverage || "",
        what_changed: c.recommendation || "",
      })),
    },
    keywords_added: {
      summary: `${keywordsAdded.length} target keyword(s) now covered after tailoring; ${(T.missing_keywords || []).length} still worth adding.`,
      coverage_percentage: kcT,
      keywords_added: keywordsAdded,
      keywords_still_missing: (T.missing_keywords || []).map((k: ClaudeMatch) => String(k.keyword)),
    },
    why_this_matters: {
      before: O.tailoring_verdict?.verdict_reasoning || "",
      after: T.tailoring_verdict?.verdict_reasoning || "",
    },
    metadata: {
      target_role: opts.jobTitle,
      target_company: opts.companyName,
      analysis_date: new Date().toISOString(),
    },
  };

  return { beforeScore, afterScore, analysisResults };
}
